import { theme as lightTheme, customScrollbar } from 'cdk-radial';
import { useSearchParams, Link } from 'react-router-dom';
import { useStore } from 'store/store';
import styled from 'styled-components';
import { filterExamplesList } from 'utils/filterExamplesList';
import * as sorts from 'utils/sortingRoutines';
import { useMouseListener } from 'utils/useMouseListener';
import { ExamplesList } from './ExamplesList';
import { Info } from './Info';
import { ListControls } from './ListControls';
import { Resizer } from './Resizer';
import { SubTitle } from './SubTitle';
import { TagCloud } from './TagCloud';
import { Title } from './Title';
import { Version } from './Version';
import pkg from '../../package.json';
import examples from '../examples-list.js';

const _versionNumber = pkg.dependencies['cdk-radial'].replace('^', '');
const _versionString = 'cdk-radial v' + _versionNumber;

const StyledLink = styled(Link)`
  text-decoration: none;
  span:hover {
    color: ${lightTheme.color.gray[200].value};
    transition: color ${({ theme }) => theme.transition.duration.value};
  }
`;

const StyledExamplesList = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'listcontrols'
    'exampleslist';
  ${ListControls} {
    grid-area: listcontrols;
    position: sticky;
    padding-top: 16px;
    padding-bottom: 4px;
    top: 0;
    background: ${({ theme }) => theme.color.additional.light.value};
  }
  ${ExamplesList} {
    grid-area: exampleslist;
  }
`;

const TagsAndInfo = styled.div``;

const Examples = styled(({ className, indexPath }) => {
  // Filter by index
  // If indexPath is undefined we are at the root.
  // At the root we only want to show one level deep. The filter with the matchAll call looks to see how many "/" are in the path and only shows if there is 1.
  // If there is an indexPath, that indexPath will be used to filter the examples. All folders under the indexPath will be shown.

  const baseExamplesList =
    indexPath !== undefined
      ? examples.filter((example) => example.path.includes(indexPath))
      : examples.filter((example) => {
          const directories = [...example.path.matchAll(/\//g)];
          return directories.length > 1 ? false : true;
        });

  // Filter by tags
  const [searchParams] = useSearchParams();
  const isAnyTagSet = searchParams.get('any') !== null;
  const examplesList = filterExamplesList({
    tagsInQuerystring: searchParams.get('tags'),
    matchAnyTag: isAnyTagSet,
    examples: baseExamplesList,
  });

  // Apply the current sort
  const examplesSort = useStore((state) => state.examplesSort);
  examplesList.sort(sorts.sortBy(examplesSort.type, examplesSort.direction));

  const headerConfig = useStore((state) => state.headerConfig);
  const nextColor = useStore((state) => state.nextColor);
  const switchTheme = useStore((state) => state.switchTheme);

  const [listingRef, resizerRef, tagsRef] = useMouseListener();

  return (
    <div className={className}>
      <header>
        <StyledLink to={'/'}>
          <Title>{headerConfig.title}</Title>
        </StyledLink>{' '}
        <SubTitle onClick={switchTheme}>
          {headerConfig.subtitle.length > 0 ? ` \u2014 ${headerConfig.subtitle}` : ''}
        </SubTitle>
        <Version onClick={nextColor}>{_versionString}</Version>
      </header>
      <main>
        <StyledExamplesList ref={listingRef}>
          <ListControls isAnyTagSet={isAnyTagSet} />
          <ExamplesList list={examplesList} />
        </StyledExamplesList>
        <Resizer ref={resizerRef} />
        <TagsAndInfo ref={tagsRef}>
          <Info />
          <TagCloud list={baseExamplesList} />
        </TagsAndInfo>
      </main>
    </div>
  );
})`
  display: grid;
  height: 100vh;
  height: 100dvh;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'header'
    'main';

  header {
    grid-area: header;
    background: ${() => {
      const headerConfig = useStore((state) => state.headerConfig);
      return headerConfig.background;
    }};
    color: ${({ theme }) => theme.color.additional.alwaysWhite.value};
    padding: 64px 16px 8px 16px;
    display: flex;
    align-items: baseline;
  }

  main {
    overflow: auto;
    grid-area: main;
    background: ${({ theme }) => theme.color.additional.light.value};
    color: ${({ theme }) => theme.color.additional.dark.value};
    font-family: 'Roboto';
    display: flex;
    grid-template-columns: 3fr 1fr;
    grid-template-areas: 'listing tags';
    gap: 0;
    padding: 0 0 16px 16px;
    & > div:first-child {
      padding-right: 12px;
    }
  }
  ${StyledExamplesList} {
    ${customScrollbar};
    grid-area: listing;
    overflow: auto;
    flex: 1 1 0%;
  }
  ${TagsAndInfo} {
    ${customScrollbar};
    flex-basis: 30%;
    grid-area: tags;
    overflow: auto;
    div:has(#searchInput) {
      margin-top: 16px;
    }

    h3 {
      position: sticky;
      padding-top: 16px;
      padding-bottom: 4px;
      top: 0;
      z-index: 10;
      margin: 0;
      background: ${({ theme }) => theme.color.additional.light.value};
      border-bottom: 1px solid ${({ theme }) => theme.color.gray[400].value};
    }
  }
  ${Version}:hover {
    cursor: pointer;
  }
`;

export default Examples;
